////////////////////////////////////////////////////////////////////////////////
// GLG Combo Chart Demo
//
// This demo demonstrates how to display, configure and update a GLG realtime
// stripchart containing multiple stacked charts that share the same Time
// axis. User interaction includes cursor feedback, interactive zooming and
// scrolling, ability to select variables to be displayed by showing or hiding
// individual charts, as well as define different sets of variables via
// configuration data.
//
// The demo is written in pure HTML5 and JavaScript. The source code of the
// demo uses the GLG Toolkit JavaScript Library supplied by the included
// Glg*.js and GlgToolkit*.js files.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0, no-unused-vars: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import { GLG, GetCurrTime, IsUndefined, ConfigureWindow, CheckElement,
         ShowElement, AppAlert, AppError, AppInfo, AppLog } from './Utils.js'
import { DemoDataFeed } from './DemoDataFeed.js'
import { LiveDataFeed } from './LiveDataFeed.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

const DRAWING_NAME = "stripchart_combo.g";
const DRAWING_NAME_MOBILE = "stripchart_combo_mobile.g";

const RANDOM_DATA = true;       /* If set to true, simulated demo data will be 
                                   used for animation using DemoDataFeed. 
                                   Set to false to enable live application 
                                   data generated by LiveDataFeed. */
const BUFFER_SIZE = 50000;      /* Number of samples to keep in the buffer for 
                                   each plot. */
const PREFILL_DATA = true;      /* Setting to false suppresses pre-filling the
                                   chart's buffer with data on start-up. */
const SUPPLY_TIME_STAMP = true; /* If set to true, the application supplies 
                                   time stamp for each data sample explicitly. 
                                   Otherwise, time stamp is automatically 
                                   generated by the chart using current time. */
/* If this flag is set to true:
   - Data samples are pushed into each chart using low level API methods, 
     allowing to increase performance when a large number of data samples is
     pushed into the chart each second.
   - SUPPLY_TIME_STAMP has to be set to true, so that the application
     explicitly supplies time stamps.
   - AutoScale feature needs to be handled manually by the application,
     as opposed to having the chart handle it automatically;
*/
const FAST_DATA_PROCESSING = false;

const USE_CONFIG_FILE = true;  /* If set to true, configuration file specified 
                                  by DEFAULT_CONFIG_FILE is used to configure
                                  charts at runtime. Otherwise, a hardcoded
                                  configuration tables specified by 
                                  DEFAULT_CONFIG_TABLE is used. */

// Sample configuration files.
const CONFIGURATION1_FILENAME = "chart_config_file1.json";
const CONFIGURATION2_FILENAME = "chart_config_file2.json";

const CONFIGURATION1_TITLE = "Configuration1";
const CONFIGURATION2_TITLE = "Configuration2";

/* Samples of hardcoded configuration record arrays containing configuration 
   data for each chart. A hardcoded configuration array may be used instead of
   configuration file if USE_CONFIG_FILE=false;
   ConfigRecord fields: 
          chart_title, yaxis_label, tag, y_low, y_high, plot_color
*/
const ConfigDataTable1 = /* ConfigRecord[] */
  [ new ConfigRecord( "Pressure", "PSI", "Pressure", 0, 100,
                      { r:0.0, g:0.4, b:0.8 }, true ),  
    new ConfigRecord( "Temperature", "C", "Temperature", 0, 100,
                      { r:0.4, g:0.8, b:0.0 }, true ),
    new ConfigRecord( "Voltage", "V", "Voltage", 0, 500,
                      { r:0.5, g:0.0, b:0.65 }, false ),
    new ConfigRecord( "Current", "Amp", "Amps", 0, 50,
                      { r:0.65, g:0.0, b:0.35 }, true )
  ];

const ConfigDataTable2 = /* ConfigRecord[] */
  [ new ConfigRecord( "RPM", "RPM", "RPM", 0, 3000,
                      { r:0.0, g:0.4, b:0.8 }, true ),  
    new ConfigRecord( "Fuel", "%", "Fuel", 0, 100,
                      { r:0.4, g:0.8, b:0.0 }, true )
  ];

/* Specifies the configuration to be loaded on startup.
   See LoadConfigurationFrom for the list of defined configurations.
*/
const INIT_CONFIGURATION_INDEX = 1;

const UPDATE_INTERVAL = 100;      /* Update interval in msec. */

/* Constants for scrolling the chart to the beginning or the end of 
   the time range.
*/
const DONT_CHANGE  = 0;
const MOST_RECENT  = 1;       /* Make the most recent data visible. */
const LEAST_RECENT = 2;       /* Make the least recent data visible.*/

// Convenience time span constants.
const  TEN_SECONDS = 10;
const  ONE_MINUTE = 60;
const  ONE_HOUR   = 3600;
const  ONE_DAY    = 3600 * 24;

/* SpanData array is used to handle the SpanSelector pulldown menu, which
   allows the user to change Span interval for the Time axis.
   When the user selects a menu item at runtime, the number of Time axis ticks,
   time format for the axis labels, etc. are adjusted as defined by the items 
   in this array.
      
   SpanInfo item format:
         label_str, tim_span, major_interval, minor_interval, 
         time_format, tooltip_format.
   
   major_interval > 0 defines absolute interval in seconds;
   major_interval < 0 defines number of major ticks.
   The same logic applies to minor_interval.
*/
const SpanData = /* SpanInfo[] */
  [ new SpanInfo( "10 SEC", TEN_SECONDS,      1,  0.1, "%H:%M:%S",    null ),
    new SpanInfo( "30 SEC", 3 * TEN_SECONDS,  5,  0.5, "%H:%M:%S",    null ),
    new SpanInfo( "1 MIN",  ONE_MINUTE,       10,   1, "%H:%M:%S",    null ),
    new SpanInfo( "10 MIN", 10 * ONE_MINUTE,  60,  10, "%H:%M%n%n%x", null ),
    new SpanInfo( "30 MIN", 30 * ONE_MINUTE, -10,  -6, "%H:%M%n%x",   null ),
    new SpanInfo( "1 HOUR", ONE_HOUR,        -10, -10, "%H:%M%n%x",   null )
  ];

const INIT_SPAN_INDEX = 2;         /* A SpanData array index that defined 
                                      the initial span of the SpanSelector 
                                      combobox widget (1min). */
const PREFILL_SPAN = ONE_HOUR;     /* Time interval that defines the amount 
                                      of data for prefilling the chart's 
                                      data history buffer on start-up. */

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the combo chart.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//////////////////////////////////////////////////////////////////////////////
export function GlgComboChart( glg_div_name, glg_path,
                               is_standalone, is_mobile )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
   
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    

   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;

   /* This object is used to pass data to the callback LoadConfigCB invoked
      after configuration data finished loading, either from configuration file
      or configuration table.
      ConfigLoadRequest fields: 
      - enabled is used to cancel the current load request if loading of 
        another configuration file was requested while the current request has 
        not finished. This may happen if the user clicks on a button to load one
        configuration and then clicks on another button to load another 
        configuration before the first configuration finished loading.
      - config_info is an object containing configuration filename
        and/or configuration table, and title.
   */
   this.ConfigLoadRequest = null;

   this.AssetsLoadedFlag = false;

   this.SupplyTimeStamp = SUPPLY_TIME_STAMP;   /* Passed to DemoDataFeed */
   
   this.LoadedConfigInfo = null;  /* Stores currently loaded configuration 
                                     information, including filename or 
                                     the hardcoded table, and title. It is used
                                     to check if the configuration changed
                                     while waiting for data.
                                  */
   this.NewConfigTitle = null;    /* String: Title of a configuration being 
                                     loaded, used for status display. */
   this.FirstTime = true;         /* Flag to indicate the initial configuration
                                     was loaded. Set to true on start-up and 
                                     reset to false after the first 
                                     configuration was loaded. */
   this.Configuration = null;     /* ConfigRecord[]: Stored configuration data.
                                   */
   this.DataFeed = null;          /* DataFeed object used for animation.
                                     Will be assigned to DemoDataFeed to use 
                                     simulated demo data (RANDOM_DATA=true),
                                     or LiveDataFeed to use live application
                                     data (RANDOM_DATA=false). */   
   this.MainViewport = null;      /* GlgObject: Top level viewport of the 
                                     loaded drawing. */
   this.NumCharts = 0;            /* int: Number of charts. */
   this.ChartArray = null;        /* ChartInfo[]: Stores information for 
                                     individual charts. */
   this.TimeAxis = null;          /* GlgObject: Object ID of the TimeAxis. */
   this.Toolbar = null;           /* GlgObject: Object ID of the Toolbar 
                                     viewport.*/
   this.ChartDialog = null;       /* GlgObject: Floating dialog allowing to 
                                     toggle visibility of individual charts at
                                     runtime. */
   this.ChartMenu = null;         /* GlgObject: ChartMenu inside ChartDialog, 
                                     used to control visibility of individual
                                     charts. */
   this.ChartMenuNumVisRows = 4;  /* int: Number of visible rows in the 
                                     ChartMenu inside ChartDialog. If number of
                                     rows exceed this number, the menu will use
                                     a scrollbar. */
   this.SpanIndex = INIT_SPAN_INDEX; /* int: Index of the currently displayed 
                                       time span. */
   this.TimeSpan = 0;             /* int: Time axis span in sec. */
   this.ScrollIncrement = 0;      /* double: Scroll increment in sec. */

   this.AutoScroll = 1;           /* int: Current auto-scroll state: enabled (1)
                                    or disabled (0). */
   this.EnableYDragging = 1;      /* int: Current state of chart dragging in
                                     Y direction: enabled (1) or disabled (0). 
                                     If disabled, chart dragging is performed 
                                     only in horizontal direction. */
   this.StoredScrollState = 0;    /* int: Stored AutoScroll state to be 
                                     restored if ZoomTo is aborted. */
   this.CursorPosition = null;    /* GlgPoint: Stores current cursor position, 
                                     used to calculate time stamp at cursor and
                                     update the cross-hair line. */
   this.StartZoomToMode = false;  /* This flag is set to true when the user 
                                     clicks on a ZoomTo button. After the user 
                                     clicks on a particular chart to select the
                                     chart to be zoomed, ZoomToMode is activated
                                     for the selected chart. */
   this.Ready = false;            /* This flag is set to true in StartGlgChart()
                                     after the charts have been configured and
                                     ready for data updates. The flag is reset 
                                     to false in ClearConfiguration(). */
   this.PrefillActive = false;    /* Will be set for the duration of prefilling
                                     the chart with historical data. */
   this.ScrollMode = !is_mobile;  /* Defines an action to perform on touch on
                                     mobile devices: 
                                     - true : scroll charts (desktop default)
                                     - false : display sample values
                                       (mobile default) */

   /* Contains a list of all tags assigned to the chart plots.
      This list is used by the DataFeed object to query real-time data.
      The array is populated in InitAfterH(). Each element has
      a property 'tag_source'. 
   */
   this.DataTagList = null;       /* [ tag_source: ] */

   // Stores time stamp of the last update, as well as current time.
   this.LastTime = 0.0;           /* double */
   this.CurrentTime = 0.0;        /* double */

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts the combo chart by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;

   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   // Show touch action selector for mobile devices.
   if( this.IsMobile )
     ShowElement( this.GLG_div_name + "_touch_action", true );
   
   /* Load assets, including:
      - GLG scrollbars (.g files).
      - GLG chart drawing, using filename defined by DRAWING_NAME.
 
      When all three assets are loaded, LoadConfiguration callback is invoked 
      to load chart configuration specified by INIT_CONFIGURATION_INDEX.

      If USE_CONFIG_FILE=true, configuration data are loaded from the specified
      file in JSON format defined by the configuration's config_file. 

      If USE_CONFIG_FILE=false, configuration data are loaded from the 
      hardcoded table defined by the configuration's config_table.

      After configuration data finished loading, the charts in the drawing get 
      configured based on the loaded configuration.
   */
   this.LoadAssets( ()=>this.LoadConfigurationFrom( INIT_CONFIGURATION_INDEX ),
                    null );
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );
   
   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.MainViewport )
     this.MainViewport.ResetHierarchy();   // Undisplay GLG drawing.

   if( this.DataFeed )
     this.DataFeed.Cleanup();              // Cleanup DataFeed.

   this.AbortPendingLoadRequests();
    
   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}

////////////////////////////////////////////////////////////////////////////// 
// Loads the configuration specified by the index parameter.
// It is also used to respond to the HTML button clicks.
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.LoadConfigurationFrom = function( /*int*/ index )
{
   /* Data object containing information about the chart configuration to be 
      loaded:
      - config_file  : filename of the configuration file in JSON format. 
                       Used if USE_CONFIG_FILE=true.
      - config_table : hardcoded configuration table. Used if 
                       USE_CONFIG_FILE=false or config_file=null.
      - config_title : Configuration title to be displayed in the status field.
   */
   let load_data;

   switch( index )
   {
    case 1:
      load_data = { config_file: CONFIGURATION1_FILENAME,
                    config_table: ConfigDataTable1,
                    config_title: CONFIGURATION1_TITLE };
      this.LoadConfiguration( load_data );
      break;

    case 2:
      load_data = { config_file: CONFIGURATION2_FILENAME,
                    config_table: ConfigDataTable2,
                    config_title: CONFIGURATION2_TITLE };
      this.LoadConfiguration( load_data );
      break;

    default: AppError( "Invalid configuration button index." ); break;
   }
}

////////////////////////////////////////////////////////////////////////////////
// Loads chart configuration data. On start-up, this function is invoked as a
// callback by LoadAssets() after the chart drawing is loaded. This function can
// be also invoked explicitly to load new configuration data and reconfigure the
// charts dynamically at run-time without reloading the drawing. The example
// invokes this function when the user clicks on one of the HTML buttons:
// Configuration1 or Configuration2.
////////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.LoadConfiguration = function( /*Object*/ config_info )
{
   Debug( "LoadConfiguration: " + config_info.config_title +
          " for: " + this.GLG_div_name );
  
   if( !this.Active )
     return;

   // Disable load requests from HTML buttons until all assets have been loaded.
   if( !this.AssetsLoadedFlag )
     return;

   if( config_info == null )
   {
      AppError( "LoadConfiguration: Null config_info." );
      return;
   }
   
   let filename = config_info.config_file;
   let config_table = config_info.config_table;
   
   if( filename == null && config_table == null )
   {
      AppError( "LoadConfiguration: Invalid configuration information." );
      return;
   }
   
   /* If the requested configuration matches currently loaded configuration, 
      do nothing to avoid loading the same configuration.
   */
   if( this.SameConfiguration( config_info ) )
     return;

   // Load new configuration.

   // Abort pending load requests, if any.
   this.AbortPendingLoadRequests();    
   
   // Create a new load request.
   this.ConfigLoadRequest = { enabled: true, config_info: config_info };
   
   // Display title of the new configuration load request.
   this.NewConfigTitle = config_info.config_title;   
   this.DisplayStatus();
   
   if( USE_CONFIG_FILE && filename )
   {
      /* Load new configuration from file.
         the LoadConfigCB callback will be invoked when the drawing has been 
         loaded.

         Using "bind( this )" as a shorter way to provide "this" compared with 
         using lambda: with bind, we do not need to specify parameter list that
         we would need to provide for lambda.
      */
      GLG.LoadAsset( this.GetFullName( filename ),
                     GLG.GlgHTTPRequestResponseType.JSON,
                     /*callback*/ this.LoadConfigCB.bind( this ),
                     /*user data*/ this.ConfigLoadRequest );
   }
   else
     /* Load new configuration from a hardcoded configuration data table.
        Invoke LoadConfigCB on a timeout to let the browser update status 
        display to indicate a new configuration is loaded.
     */
     setTimeout( this.LoadConfigCB( config_table,
                                    this.ConfigLoadRequest ).bind( this ), 30 );
       
}

////////////////////////////////////////////////////////////////////////
// This callback is invoked when a configuration file finished loading.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.LoadConfigCB =
  function( /*ConfigRecord[]*/ config_data, /*Object*/ user_data )
{
   if( !this.Active )
     return;
      
   let load_request = user_data;
   if( !load_request.enabled )
     /* This load request was aborted by requesting to load another 
        configuration before this load request has finished.
     */
     return;

   if( config_data == null || config_data.length == 0 )
   {
      /* Keep previously loaded configuration (if any), display status info and 
         generate an error.
      */
      AppError( "Configuration loading failed: " + this.NewConfigTitle );
      this.NewConfigTitle = null;
      this.DisplayStatus();
      return;
   }
   
   // Configuration data are loaded: disable spinning loader.
   RemoveElement( this.GLG_div_name, "loader_container" );

   // Store currently loaded configuration information.
   this.LoadedConfigInfo = load_request.config_info;
   
   // Reset: we are done with this request.
   this.ConfigLoadRequest = null;
   
   // Clear current configuration, if any.
   this.ClearConfiguration();

   // Store new configuration.
   this.Configuration = config_data;

   // Start GLG chart based on loaded configuration. 
   this.StartComboChart();

   // Update status info: remove "Loading ..."
   this.NewConfigTitle = null;   
   this.DisplayStatus();
}

//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.StartComboChart = function()
{
   if( this.Configuration == null )
   {
      AppError( "Invalid chart configuration." );
      return;
   }
   
   /* Set number of charts before hierarchy setup based on the loaded 
      configuration.
   */
   if( !this.ConfigureChartsBeforeH() )
      return;
   
   // Setup object hierarchy in the drawing.
   this.MainViewport.SetupHierarchy();
   
   // Store current time.
   this.CurrentTime = GetCurrTime();

   // Demo only: Initialize LastTime to the current time.
   this.LastTime = this.CurrentTime;
   
   // Configure charts after hierarchy setup.
   this.ConfigureChartsAfterH();

   // Display the drawing on a web page.
   this.MainViewport.Update();

   /* Set the flag to indicate the charts are configured and ready for
      data updates.
   */
   this.Ready = true;

   // Start dynamic updates upon startup.
   if( this.FirstTime )
   {
      this.FirstTime = false;   // First time: reset the flag.   
      this.GetData();
   }
}

//////////////////////////////////////////////////////////////////////////////
// Initialization performed upon drawing loading. Invoked from AssetLoadedCB.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.Init = function( /*GlgObject*/ drawing )
{
   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }
   
   // Add DataFeed object used to animate the drawing with real-time data.
   this.AddDataFeed();
   
   // Store loaded drawing viewport.
   this.MainViewport = drawing;
   
   // Define the HTML element in a web page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
   
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
   
   // Add Input and Trace callbacks.
   this.MainViewport.AddListener( GLG.GlgCallbackType.INPUT_CB,
                                  this.InputCallback.bind( this ) );
   this.MainViewport.AddListener( GLG.GlgCallbackType.TRACE_CB,
                                  this.TraceCallback.bind( this ) );
    
   // Retrieve object ID of the Toolbar viewport.
   this.Toolbar = this.MainViewport.GetResourceObject( "Toolbar" );
   if( this.Toolbar == null )
     AppError( "Toolbar object not found. " );

   // Initialize ChartDialog and ChartMenu.
   this.InitChartDialog();
    
   // Store object ID of the Time axis.
   this.TimeAxis = this.MainViewport.GetResourceObject( "TimeAxis" );

   /* Enable automatic layout adjustment for the ChartSeries when
      individual charts change their visibility: if one of the charts
      becomes invisible, the height of the other charts is adjusted
      automatically to occupy the vacant space.
   */
   this.MainViewport.SetDResource( "ChartSeries/HandleInvisible",
                                   GLG.GlgHandleInvisible.SHIFT_ROWS );
   this.MainViewport.SetDResource( "ChartSeries/FillSpace",
                                   GLG.GlgFillSpace.ADJUST_ROW_HEIGHT );
}

////////////////////////////////////////////////////////////////////////
// Initialize ChartDialog and ChartMenu variables.
// Set ChartDialog size and position, as well as make it a floating dialog.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.InitChartDialog = function()
{
   /* Store object ID of the ChartDialog viewport used to select variables
      to be plotted.
   */
   this.ChartDialog = this.MainViewport.GetResourceObject( "ChartDialog" );
   if( this.ChartDialog == null )
   {
       AppInfo( "ChartDialog not found." );
       return;
   }

   // Store object ID of the ChartMenu inside ChartDialog.
   this.ChartMenu = this.ChartDialog.GetResourceObject( "ChartMenu" );
   if( this.ChartMenu == null )
     AppError( "ChartMenu not found." );

   // Make it a floating dialog.
   this.ChartDialog.SetDResource( "LineWidth", 0.0 );
   this.ChartDialog.SetDResource( "ShellType", GLG.GlgShellType.DIALOG_SHELL );

   // Set dialog title. 
   this.ChartDialog.SetSResource( "ScreenName", "Chart Selection" );
   
   // Set dialog size and position.
   this.SetDialogSizeAndPosition( this.ChartDialog );
      
   // Make the dialog visible on start up.
   ShowDialog( this.ChartDialog, true );
}
   
//////////////////////////////////////////////////////////////////////////////
// Create new ChartArray based on the stored Configuration (if any).
// Must be invoked after hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ConfigureChartsAfterH = function()
{
   if( this.MainViewport == null ||
       this.Configuration == null || this.NumCharts == 0 )
   {
      AppError( "Chart configuration failed." );
      this.ClearConfiguration();
      return;
   }
    
   // Retrieve ChartSeries object.
   let chart_series = /* GlgObject */
     this.MainViewport.GetResourceObject( "ChartSeries" );    
   if( chart_series == null )
   {
      AppError( "ChartSeries not found. Chart configuration failed." );
      this.ChartArray = null;
      return;
   }
    
   /* Create new ChartArray and populate it with chart information based 
      on the provided chart configuration.
   */
   this.ChartArray = [];
   for( let i=0; i<this.NumCharts; ++i )
   {
      // Retrieve objectID of each chart's viewport.
      let chart_vp = /* GlgObject */
        chart_series.GetResourceObject( "ChartViewport" + i );

      // Creates and fills a ChartInfo record.
      let chart_info = InitializeChart( chart_vp, this.Configuration[i] );

      /* Initialize vertical line that moves with the mouse and stores
         its object ID as the cross_hair_line property of chart_info.
         This can be done only for the first chart, since attributes of 
         all vertical lines in all charts are constrained.
      */
      if( i == 0 )
        chart_info.InitCrossHairLine();
      
      this.ChartArray.push( chart_info );
   }
    
   // Configure Chart menu in the ChartDialog, if any.
   this.InitChartMenuAfterH();

   /* Populate SpanSelector option menu with items based on the SpanData array.
      The menu allows to select a desired time span for the TimeAxis.
      Menu initialization must be done after hierarchy setup.
      
      The demo uses the same SpanData array for both Configuration1 and
      Confgiguration2, but the application may extend the code to supply 
      a different SpanData array for each configuration. 
   */
   this.InitSpanSelector();
 
   // Enable AutoScroll, both for the toggle button and the chart.
   this.ChangeAutoScroll( 1 );

   // Disable Y Dragging by default.
   this.ChangeYDragging( 0 );

   // Set time span of the TimeAxis.
   this.SetChartSpan( this.SpanIndex );
    
   // Store valid tag sources in DataTagList array.
   this.CreateDataTagList();

   if( PREFILL_DATA )
     this.PrefillChartsWithData();
}

////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.PrefillChartsWithData = function()
{
   if( this.NumCharts == 0 )
     return;

   /* Prefill each chart's history buffer with data. The demo prefills
      the buffer with data up to the current time.
      */
   for( let i=0; i<this.NumCharts; ++i )
     this.FillChartHistory( this.ChartArray[i] );
}

//////////////////////////////////////////////////////////////////////////////
// Create DataFeed object used to animate the chart with data.
// Simulated data are provided by DemoDataFeed. The application should provide
// a custom implementation of LiveDataFeed.js to supply real-time application
// data for the chart.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.AddDataFeed = function()
{
   if( RANDOM_DATA )
   {
      this.DataFeed = new DemoDataFeed( this );
      AppLog( "Using simulated DemoDataFeed." );
   }
   else
   {
      this.DataFeed = new LiveDataFeed();
      AppLog( "Using LiveDataFeed." );
   }
}

//////////////////////////////////////////////////////////////////////////////
// Create DataTagList array containing a list of all tags for the chart plots.
// This list is passed by DataFeed.ReadData to the server to request new
// data for the tags of interest. This list gets recreated whena new chart
// configuration is loaded.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.CreateDataTagList = function()
{
   if( this.ChartArray == null || this.ChartArray.length == 0 )
   {
      this.DataTgagList = null;
      return;
   }

   this.DataTagList = [];   /* String[] */
   for( let i=0; i<this.ChartArray.length; ++i )
   {
      // Store only valid tag sources, skipping unassigned tags.
      if( !IsUndefined( this.ChartArray[i].plot_info.tag ) )
        this.DataTagList.push( { tag: this.ChartArray[i].plot_info.tag } );
   }
}

////////////////////////////////////////////////////////////////////////
// Chart initialization before hierarchy setup.
// Set the number of stacked charts. The number of charts may be changed 
// dynamically at run-time.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ConfigureChartsBeforeH = function() /* boolean */
{
   if( this.MainViewport == null )
     return false;

   let chart_series = this.MainViewport.GetResourceObject( "ChartSeries" );
   if( chart_series == null )
   {
      AppError( "ChartSeries not found, SetNumCharts failed." );
      this.NumCharts = 0;
      return false;
   }
   
   // Set number of charts in the drawing.
   this.NumCharts = this.Configuration.length;
   chart_series.SetDResource( "NumCharts", this.NumCharts );   
   
   /* Set BUFFER_SIZE for the series' template before hierarchy setup. This
      setting will be inherited by all series' instances (individual charts)
      when the instances get created during hierarchy setup.
   */
   chart_series.SetDResource( "ChartViewport/Chart/BufferSize", BUFFER_SIZE );
   
   /* Set number of menu items in the ChartDialog to match the number
      of charts.
   */
   this.InitChartMenuBeforeH();
   
   return true;
}

//////////////////////////////////////////////////////////////////////////////
// Set number of rows in the ChartMenu inside ChartDialog to match the
// number of charts. This function is invoked before hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.InitChartMenuBeforeH = function()
{
   if( this.ChartMenu == null || this.NumCharts <= 0 )
     return;

   // Set number of menu rows.
   this.ChartMenu.SetDResource( "NumRows", this.NumCharts );

   // Adjust number of visible rows.
   this.ChartMenu.SetDResource( "NumVisibleRows", this.ChartMenuNumVisRows );
}

/////////////////////////////////////////////////////////////////////////
// Popuplate ChartMenu inside ChartDialog using data from Configuration[]
// array. This function is invoked after hierarchy setup.
/////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.InitChartMenuAfterH = function()
{
   if( this.ChartMenu == null )
     return;
    
   /* Populate chart menu with labels using Configuration array and set
      the state of each toggle button based on the specified initial visibility.
   */
   for( let i=0; i<this.NumCharts; ++i )
   {
      let button =  /* GlgObject */
        this.ChartMenu.GetResourceObject( "Button" + i );
      button.SetSResource( "LabelString", this.Configuration[i].chart_title );
      button.SetDResource( "OnState",
                           this.Configuration[i].init_visibility ? 1 : 0 );
   }
}

////////////////////////////////////////////////////////////////////////
// Show/hide a dialog.
////////////////////////////////////////////////////////////////////////
function ShowDialog( /*GlgObject*/ dialog, /*boolean*/ show )
{
   if( dialog )
     dialog.SetDResource( "Visibility", show ? 1.0 : 0.0, true /*if changed*/ );
}

////////////////////////////////////////////////////////////////////////////// 
// Pre-fill the graph's history buffer with data. 
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.FillChartHistory = function( /*ChartInfo*/ chart_info )
{
   if( chart_info == null )
     return;
     
   /* Fill the amount of data requested by the PREFILL_SPAN, up to the 
      available chart's buffer size.
      Add an extra second to avoid rounding errors.
   */
   let num_seconds = PREFILL_SPAN + 1;       /* int */

   /* Calculate maximum number of seconds for prefilling the chart based
      on the chart's BufferSize. Add an extra second to avoid rounding errors.
   */
   let interval = this.GetSampleInterval();       /* double */
   let max_num_sec = BUFFER_SIZE * interval + 1;  /* double */ 
   
   if( num_seconds > max_num_sec )
     num_seconds = max_num_sec;
   
   let start_time = this.CurrentTime - num_seconds; /* double */
   let end_time = this.CurrentTime;   /* double: Stop at the current time. */
   
   /* This example uses one plot per chart. The code may be extended to support
      multiple plots in each chart. For the demo datafeed, the PlotHistDataCB 
      callback will be invoked synchronously inside GetHistPlotData().
   */
   this.PrefillActive = true;
   this.DataFeed.GetHistPlotData( chart_info.plot_info, start_time, end_time,
                                  /*callback*/ this.PlotHistDataCB.bind( this ),
                                  /*user data*/ chart_info.plot_info );
}

////////////////////////////////////////////////////////////////////////////// 
// Fills a plot with data from the provided data array.
// For increased performance of prefilling a chart with large quantities of
// data, the data are pushed into the plot using a low level API method
// AddPlotDataSample.
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.PlotHistDataCB =
  function( /*PlotDataPoint[]*/ data_array, /*PlotInfo*/ plot_info )
{
   this.PrefillActive = false;
    
   if( !this.Active || plot_info == null )
     return;
   
   if( data_array == null || data_array.length == 0 )
   {
      AppLog( "No historical data for " + plot_info.tag );
      return;
   }

   /* Push data samples to the plot's buffer, using quick mode to
      speed up prefilling of the chart.
   */
   let num_samples = data_array.length;
   for( let i=0; i<num_samples; ++i )
   {
      let data_point = data_array[i];   /* PlotDataPoint */
      PushPlotPointDirect( plot_info, data_point, /*quick mode*/ true );
   }
}

//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.GetData = function()
{
   if( !this.Active )
     return;
   
   if( this.DataFeed == null )
   {
      AppError( "Null DataFeed object." );
      return;
   }

   if( !this.Ready || this.DataTagList == null || this.PrefillActive )
   {
      // The chart is not ready: restart the timer with a shorter time interval.
      setTimeout( this.GetData.bind( this ), 30 );
      return;
   }

   /* Demo: Store current time. It is used to generate a data sample's 
      time stamp in demo mode when data come in packets.
   */
   this.CurrentTime = GetCurrTime();
   
   /* Obtain new real-time data values for all plots in the chart
      and invoke GetDataCB callback when done. Tags assigned to the
      ValueEntryPoint of each plot represent data source variables
      to query real-time values for.
   */
   this.DataFeed.ReadData( this.DataTagList,
                           /*callback*/ this.GetDataCB.bind( this ),
                           /*user data*/ this.LoadedConfigInfo );
}

//////////////////////////////////////////////////////////////////////////
// Data query callback. It is invoked by the DataFeed after the new data 
// are received from the server.
//////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.GetDataCB =
  function( /*DataRecord[]*/ new_data, /*Object*/ config_info )
{
   if( !this.Active )
     return;

   /* Ignore new data if the configuration filename has changed and a 
      new configuration has been loaded. This will stop further queries 
      for the old configuration.
   */
   if( config_info != this.LoadedConfigInfo )
   {
      /* When a new configuration is loaded, restart the timer with a shorter
         time interval.
      */
      setTimeout( this.GetData.bind( this ), 30 );
      return;
   }
   
   // Push new data to the chart.
   this.UpdateChart( new_data );
    
   // Send new data query request.
   setTimeout( this.GetData.bind( this ), UPDATE_INTERVAL );
}

//////////////////////////////////////////////////////////////////////////////
// Push new data into graphics. For each tag in new_data array, find a
// a record in PlotArray with a matching tag, store the new value, 
// and push new value into graphics.  
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.UpdateChart = function( /*DataRecord[]*/ new_data )
{
   if( new_data == null || new_data.length == 0 )
   {
      AppInfo( "No new data received." );
      return;
   }

   for( let i=0; i<new_data.length; ++i )
   {
      let data_record = new_data[i];  /* DataRecord */

      if( data_record.data_array == null || data_record.data_array.length == 0 )
        continue; // No data received.

      /* Find a chart_info record in ChartArray that has a tag matching 
         data_record.tag.
      */
      let chart_info = this.LookupChartArray( data_record.tag );
        
      // Chart record not found, don't use this data point.
      if( chart_info == null )
      {
         AppError( "Discarding data point, plot not found for tag = "
                   + data_record.tag );
         continue;
      }

      // Push data into the found chart's plot. 
      this.PushPlotData( chart_info.plot_info, data_record.data_array );
            
      // Setup chart hierarchy to prepare it for the time line update.
      chart_info.chart.SetupHierarchy();
   }

   /* Update the cross-hair line to follow the mouse cursor. 
      If the cursor is outside one of the charts or the time axis, 
      the cross-hair line will not be disabled.
   */
   this.DisplayCrossHairLine( this.CursorPosition );

   // Refresh display.
   this.MainViewport.Update();
      
   // Demo: Store time of the last update.
   this.LastTime = this.CurrentTime;
}

///////////////////////////////////////////////////////////////////////
// Push new data samples into the specified plot.
///////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.PushPlotData =
  function( /*PlotInfo*/ plot_info, /*PlotDataPoint[]*/ data_array )
{
   if( plot_info == null || data_array == null )
     return;
   
   const QUICK_MODE = true;      
   let num_samples = data_array.length;
   for( let i=0; i<num_samples; ++i )
   {
      let data_point = data_array[i];

      if( FAST_DATA_PROCESSING && SUPPLY_TIME_STAMP )
      {
         /* For performance optimiziation, low level API methods may be used
            to push data into the pliot's data buffer. Use QUICK_MODE=true 
            to speed-up the process even further. However, it will require the
            application to advance the time axis and handle auto-scaling 
            manualily.
         */
         PushPlotPointDirect( plot_info, data_point, QUICK_MODE );
      }
      else
        PushPlotPoint( plot_info, data_point );
   }

   // If quick mode is used, advance the time axis explicitly.
   if( QUICK_MODE && FAST_DATA_PROCESSING && SUPPLY_TIME_STAMP )
     this.TimeAxis.SetDResource( "EndValue",
                                 data_array[ num_samples - 1 ].time_stamp );
}

///////////////////////////////////////////////////////////////////////
// Push the new data sample into the plot.
///////////////////////////////////////////////////////////////////////
function PushPlotPoint( /*PlotInfo*/ plot_info, /*PlotDataPoint*/ data_point )
{
   if( plot_info == null || data_point == null )
     return;
    
   // Supply plot value via ValueEntryPoint.
   plot_info.value_ep.SetDResource( null, data_point.value );
    
   /* Push time stamp if provided. Otherwise, the chart will
      automatically use current time as a time stamp for the X axis.
   */
   if( SUPPLY_TIME_STAMP && data_point.time_stamp != null )
     plot_info.time_ep.SetDResource( null, data_point.time_stamp );

   // Set plot's ValidEntryPoint.
   plot_info.valid_ep.SetDResource( null, data_point.value_valid ? 1. : 0. );
}

///////////////////////////////////////////////////////////////////////
// Push the new data sample into the plot. For increased performance,
// the data are pushed into the plot using a low level API method
// AddPlotDataSample.
///////////////////////////////////////////////////////////////////////
function PushPlotPointDirect( /*PlotInfo*/ plot_info,
                              /*PlotDataPoint*/ data_point,
                              /*boolean*/ quick_mode )
{
   if( plot_info == null || data_point == null )
     return;

   /* Display markers for plots that contain markers, such as plots with 
      PlotType = MARKERS or LINE_AND_MARKERS.
   */
   let marker_visibility = 1;
   
   // Using quick mode to speed-up prefilling of the chart.
   plot_info.plot.AddPlotDataSample( data_point.value, data_point.time_stamp,
                                     data_point.value_valid, marker_visibility,
                                     /*quick mode*/ quick_mode );
}

//////////////////////////////////////////////////////////////////////////////
// Find an item in the ChartArray[] which has a plot with the tag matching
// the specified tag parameter.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.LookupChartArray =
  function( /*String*/ tag ) /* ChartInfo */
{
   if( this.ChartArray == null )
     return null;

   for( let i=0; i<this.NumCharts; ++i )
   {
      let plot_info = this.ChartArray[i].plot_info; 
      if( plot_info.tag == tag )
        return this.ChartArray[i];
   }

   return null; // not found.
}

////////////////////////////////////////////////////////////////////////
// Initialize SpanSelector drop-down menu used to change the time span
// on the TimeAxis. The menu is populated with items dynamically based
// on the SpanData array.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.InitSpanSelector = function()
{
   if( SpanData == null )
     return;

   let menu = this.Toolbar.GetResourceObject( "SpanSelector" ); /* GlgObject */
   if( menu == null )
   {
      AppError( "SpanSelector not found." );
      return;
   }

   // Clear current item list, if any.
   menu.SendMessageToObject( "Handler", "SetItemList", /*empty list*/ null,
                             null, null, null );
    
   // Number of menu items.
   let num_items = SpanData.length;        /* int */
   for( let i=0; i<num_items; ++i )
   {
      let item = SpanData[i].label;
      menu.SendMessageToObject( "Handler", "AddItem", item,
                                GLG.CreateGlgInteger( GLG.GlgAccessType.BOTTOM ),
                                null, null );
   }

   menu.SendMessageToObject( "Handler", "UpdateItemList",
                             null, null, null, null );
}

////////////////////////////////////////////////////////////////////////
// Delete current configuration. For example, clear current configuration
// prior to creating new configuration.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ClearConfiguration = function()
{
   this.ChartArray = null;
   this.NumCharts = 0;
   this.Configuration = null;
   this.DataTagList = null;
   this.Ready = false;
   this.DataFeed.ClearDataFeed();
}

//////////////////////////////////////////////////////////////////////////////
// Return true if the requested configuration config_info is the same
// as curerntly loaded configuration LoadedConfigInfo. Otherwise, returns false.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SameConfiguration =
  function( /*Object*/ config_info ) /* boolean */
{
   let filename = config_info.config_file;
   let config_table = config_info.config_table;
   
   if( !this.LoadedConfigInfo )
     return false;
   
   if( USE_CONFIG_FILE && filename )
     return filename == this.LoadedConfigInfo.config_file;
   else
     return ( config_table != null &&
              config_table == this.LoadedConfigInfo.config_table );
}

////////////////////////////////////////////////////////////////////////////// 
// Cancels any pending load requests.
////////////////////////////////////////////////////////////////////////////// 
GlgComboChart.prototype.AbortPendingLoadRequests = function()
{
   if( this.ConfigLoadRequest != null )
   {
      this.ConfigLoadRequest.enabled = false;
      this.ConfigLoadRequest = null;
   }
}

//////////////////////////////////////////////////////////////////////////////
// Handle user interaction.
// Each ChartViewport% in ChartSeries has OwnsInputCB=true, so
// InputCallback is invoked with the viewport ID of the ChartViewport%
// where an input event occurred, such as chart zoom or drag event.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.InputCallback =
  function( /*GlgObject*/ viewport, /*GlgObject*/ message_obj )
{
   if( !this.Active )
     return;
    
   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
   let subaction = message_obj.GetSResource( "SubAction" );

   // Handle window closing.
   if( format == "Window" && action == "DeleteWindow" )
   {
      if( origin == "ChartDialog" )
      {
         // Hide ChartDialog.
         ShowDialog( this.ChartDialog, false );
         viewport.Update();
      }
   }
    
   // Process button events.
   else if( format == "Button" )
   {	 
      if( action != "Activate" &&        /* Not a push button */
          action != "ValueChanged" )     /* Not a toggle button */
        return;
        
      // Abort ZoomTo mode, if any.
      this.AbortZoomTo();
        
      if( origin == "ToggleAutoScroll" )
      {         
         /* Set Chart AutoScroll based on the ToggleAutoScroll 
            toggle button setting.
         */
         this.ChangeAutoScroll( -1 ); 
      }
      if( origin == "ToggleYDragging" )
      {         
         /* Enable/Disable Y Dragging based on the ToggleYDragging toggle 
            button setting.
         */
         this.ChangeYDragging( -1 ); 
      }
      else if( origin == "ZoomTo" )
      {
         /* Start ZoomTo operation. Wait for the mouse click in the
            TraceCallback to activate ZoomTo for the selected chart 
            that received the click.
         */
         this.StartZoomToMode = true;
      }
      else if( origin == "ResetZoom" )
      {         
         // Set initial time span and reset initial Y ranges.
         this.SetChartSpan( this.SpanIndex );  
         this.RestoreInitialYRanges();

         // Disable Y Dragging on zoom reset (1:1 button).
         this.ChangeYDragging( 0 );
      }
      else if( origin == "ScrollBack" || origin == "ScrollForward" )
      {
         this.ChangeAutoScroll( 0 );
            
         let end_value = /* double */
           this.TimeAxis.GetDResource( "EndValue" ); 

         if( origin == "ScrollBack" )
           end_value -= this.ScrollIncrement;
         else
           end_value += this.ScrollIncrement;

         this.TimeAxis.SetDResource( "EndValue", end_value );
      }
      else if( origin == "ScrollToRecent" )
      {
         // Scroll to show most recent data.
         this.ScrollToDataEnd( MOST_RECENT, true );
      }
      else if( origin == "ShowHideCharts" )
      {
         /* Display ChartDialog when the user clicked on the
            Configure toolbar button.
         */
         ShowDialog( this.ChartDialog, true );
      }
        
      this.MainViewport.Update();  // format="Button"
   }

   else if( format == "Option" )
   {
      if( action != "Select" )
        return;
        
      // Abort ZoomTo mode, if any.
      this.AbortZoomTo();

      /* Handle events from the SpanSelector menu allowing to select time 
         interval for the Time axis.
      */
      if( origin == "SpanSelector" )    /* Span change */
      { 
         this.SpanIndex =
           Math.trunc( message_obj.GetDResource( "SelectedIndex" ) );
            
         this.SetChartSpan( this.SpanIndex );
         this.RestoreInitialYRanges(); // Restore in case the chart was zoomed.
            
         /* Scroll to show the recent data to avoid showing an empty chart
            if user scrolls too much into the future or into the past.
            Invoke ScrollToDataEnd() even if AutoScroll is True to 
            scroll ahead by a few extra seconds to show a few next updates
            without scrolling the chart.
         */
         let min_max =    /* GlgMinMax */
           this.ChartArray[0].chart.GetChartDataExtent( null, /*x extent*/ true,
                                                        /*query all samples*/ false );
            
         if( min_max != null )
         {
            let first_time_stamp = min_max.min;   /* double */
            let last_time_stamp = min_max.max;    /* double */
            let displayed_time_end =    /* double */
              this.TimeAxis.GetDResource( "EndValue" );
                
            if( this.AutoScroll != 0 )
              this.ScrollToDataEnd( MOST_RECENT, true );
                
            else if( displayed_time_end >
                     last_time_stamp + this.GetExtraSeconds() )
              this.ScrollToDataEnd( MOST_RECENT, true );
                
            else if( displayed_time_end - this.TimeSpan <= first_time_stamp )
              this.ScrollToDataEnd( LEAST_RECENT, true );
                
            this.MainViewport.Update();
         }
      }
   }

   // Handle events from GlgMenu.
   else if( format == "Menu" )
   {
      if( origin == "ChartMenu" )
      {
         // Handle events from the ChartMenu inside ChartDialog.
         let button_index = /* int */
           Math.trunc( message_obj.GetDResource( "SelectedIndex" ) );
         let menu = message_obj.GetResourceObject( "Object" ); /* GlgObject */
         this.ProcessChartMenuSelection( menu, button_index );
      }
        
      this.MainViewport.Update();
   }
    
   // Handle custom MouseClick events.
   else if( format == "CustomEvent" )
   {
      let event_label = message_obj.GetSResource( "EventLabel" ); /* String */
      if( event_label == null )
        return;
          
      /* Hide ChartDialog when the user clicked on the Close button inside 
         the dialog (Close button has Custom MouseEvent with 
         EventLabel="CloseChartDialog").
      */
      if( event_label == "CloseChartDialog" )
        ShowDialog( this.ChartDialog, false );

      this.MainViewport.Update();
   }
    
   else if( format == "Chart" && action == "CrossHairUpdate" )
   {
      /* To avoid slowing down real-time chart updates, invoke Update() 
         to redraw cross-hair only if the chart is not updated fast 
         enough by the timer.
      */
      if( UPDATE_INTERVAL > 100 )
        this.viewport.Update();         
   }          
    
   else if( action == "Zoom" )    // Zoom events
   {
      if( subaction == "Start" )
      {
         // Store current AutoScroll state to restore it if ZoomTo is aborted.
         this.StoredScrollState = this.AutoScroll;
      }
      else if( subaction == "ZoomRectangle" )
      {
         // Stop scrolling when ZoomTo action is started.
         this.ChangeAutoScroll( 0 );
         this.MainViewport.Update();
      }
      else if( subaction == "End" )
      {
         /* No additional actions on finishing ZoomTo. The Y scrollbar 
            appears automatically if needed: it is set to GLG_PAN_Y_AUTO. 
            Don't resume scrolling: it'll scroll too fast since we zoomed 
            in. Keep it still to allow inspecting zoomed data.
         */
      }
      else if( subaction == "Abort" )
      {
         // Resume scrolling if it was on.
         this.ChangeAutoScroll( this.StoredScrollState ); 
         this.MainViewport.Update(); 
     }
   }
   else if( action == "Pan" )                  // Pan events
   {
      /* This code may be used to perform custom action when dragging the 
         chart's data with the mouse.
      */
      if( subaction == "Start" )              // Chart dragging start
      {
      }
      else if( subaction == "Drag" )          // Dragging
      {
         /* Refresh display when the chart is dragged to ensure the cross-hair
            line and values in annotations coinside with the cursor position.  
         */
         this.MainViewport.Update();
      }
      else if( subaction == "ValueChanged" )   // Scrollbars
      {
      }
      /* Dragging ended or aborted. */
      else if( subaction == "End" || subaction == "Abort" )
      {
      }
   }
}

//////////////////////////////////////////////////////////////////////////////
// Used to obtain coordinates of the mouse click, as well as handle native
// low level events as needed.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.TraceCallback =
  function( /*GlgObject*/ viewport, /*GlgTraceData*/ trace_info )
{
   if( !this.Active )
     return;

   // Obtain the viewport where the event occurred.
   let event_vp = trace_info.viewport;            /* GlgObject */
   let vp_name = event_vp.GetSResource( "Name" ); /* String */
    
   let event_type = trace_info.event_type;        /* GlgEventType */
   let x, y;                                      /* double */
   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
      if( vp_name.startsWith( "ChartViewport" ) )
        GLG.SetTouchMode();     /* Enable chart dragging via touch events. */
      /* Fall through */
       
    case GLG.GlgEventType.TOUCH_MOVED:
      if( !GLG.GetTouchMode() )
        return;
      /* falls through */
      
    case GLG.GlgEventType.MOUSE_PRESSED:
    case GLG.GlgEventType.MOUSE_MOVED:
      // Retrieve mouse coordinates.
      x = trace_info.mouse_x * this.CoordScale;
      y = trace_info.mouse_y * this.CoordScale;
         
      /* COORD_MAPPING_ADJ is added to the cursor coordinates for precise
         pixel mapping.
      */
      x += GLG.COORD_MAPPING_ADJ;
      y += GLG.COORD_MAPPING_ADJ;
      break;

    default: break;
   }
   
   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      /* Handle only events that occur in one of the chart viewports
         with the name beginning with "ChartViewport".
      */
      if( !vp_name.startsWith( "ChartViewport" ) )
        return;

      if( this.StartZoomToMode )
      {
         event_vp.SetZoom( null, 't', 0.0 );
         this.StartZoomToMode = false;
         return;  // ZoomTo or dragging mode in progress.
      }

      // If ZoomTo mode is in progress for any of the charts, do nothing else.
      let zoom_vp = this.ZoomToMode();  /* GlgObject */
      if( zoom_vp != null )
        return; // ZoomTo or dragging mode in progress.

      // Start scrolling on mobile devices only in ScrollMode.
      if( !this.ScrollMode )
        return;
      
      /* Start dragging with the mouse when the user clicked in one of the 
         chart areas. If Y dragging is enabled, dragging is activated 
         in both X and Y direction ('s'). Otherwise, dragging is activated 
         only in X direction ('>').
      */
      let drag_type = ( this.EnableYDragging == 1 ? 's' : '>' ); 
      event_vp.SetZoom( null, drag_type, 0.0 );
      
      // Disable AutoScroll not to interfere with dragging.
      this.ChangeAutoScroll( 0 );
      this.MainViewport.Update();
      break;
        
    case GLG.GlgEventType.TOUCH_MOVED:
    case GLG.GlgEventType.MOUSE_MOVED:
      /* Handle only events that occur in one of the chart viewports 
         with the name beginning with "ChartViewport", or in the 
         top level $Widget viewport that contains TimeAxis.
      */
      if( !vp_name.startsWith( "ChartViewport" ) &&
          !vp_name.startsWith( "$Widget" ) )
      {
         this.CursorPosition = null;
         return;
      }
        
      // Store cursor position.
      this.CursorPosition = GLG.CreateGlgPoint( x, y, 0.0 );
        
      /* If the mouse moves along the Time axis (which is a child of 
         $Widget), convert cursor position relatively to one of the 
         chart viewports, since we later use the chart (not TimeAxis)
         to obtain a time stamp at cursor. Since we take into consideration
         only horizontal position of the cursor, and all charts are aligned
         vertically and have the same width, we can convert the coordinates
         relatively to the first chart viewport. The cursor X coordinate in
         the $Widget viewport differs from X coordinate in the chart 
         viewports by the border width of the chart viewport, therefore 
         the cursor position needs to be converted to the chart viewport.
      */
      if( vp_name.startsWith( "$Widget" ) )  
        GLG.TranslatePointOrigin( event_vp, this.ChartArray[0].chart_vp,
                                  this.CursorPosition );
        
      /* Display vertical time line at cursor position if there is no
         zooming or dragging mode in progress.
      */
      if( this.ZoomToMode() == null ) 
        this.DisplayCrossHairLine( this.CursorPosition );
      break;
                
    case GLG.GlgEventType.MOUSE_EXITED:
      this.CursorPosition = null;
      this.DisplayCrossHairLine( this.CursorPosition ); // Erase crosshair.
      break;
        
    default: break;
   }
}

///////////////////////////////////////////////////////////////////////////////
// Process item selection in the ChartMenu located in the ChartDialog.
// Show/Hide individual charts based on the state of the selected menu item.
///////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ProcessChartMenuSelection =
  function( /*GlgObject*/ chart_menu, /*int*/ index )
{
   if( chart_menu == null || index < 0 || index >= this.NumCharts )
     return;
    
   let button =  /* GlgObject */
     chart_menu.GetResourceObject( "Button" + index );
   let state =   /* int */
     Math.trunc( button.GetDResource("OnState") );
    
   // Show/Hide individual chart based on the menu item selection.
   this.ChartArray[index].ShowChart( state == 0 ? false : true );
}

//////////////////////////////////////////////////////////////////////////////
// Scroll to the end of the data history buffer.
// All charts have XAxis/Span and XAxis/EndValue constrained, so the
// charts scroll together. TimeAxis/Span and TimeAxis/EndValue are also
// constrained to the charts' XAxis/Span and XAxis/EndValue repspectvely.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ScrollToDataEnd =
  function( /*int*/ data_end, /*boolean*/ show_extra_sec )
{
   let end_value, extra_sec;   /* double */
    
   if( data_end == DONT_CHANGE )
     return;
    
   // Use the first chart to obtain data extent.
   let min_max =   /* GlgMinMax */
     this.ChartArray[ 0 ].chart.GetChartDataExtent( null, /*x extent*/ true,
                                                    /*query all samples*/ false ); 
   if( min_max == null )
     return;
    
   if( show_extra_sec )   
     extra_sec = this.GetExtraSeconds();
   else
     extra_sec = 0.0;
    
   if( data_end == MOST_RECENT )
     end_value = min_max.max + extra_sec;
   else   /* LEAST_RECENT */
     end_value = min_max.min - extra_sec + this.TimeSpan ;
    
   this.TimeAxis.SetDResource( "EndValue", end_value );
}

//////////////////////////////////////////////////////////////////////////////
// Change chart's AutoScroll mode, may be invoked from an HTML button.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ChangeAutoScroll = function( /*int*/ new_value )
{
   if( !this.Toolbar )
     return;   /* The drawing has not been loaded yet. */
   
   if( new_value == -1 )  // Use the state of the ToggleAutoScroll button.
   {
      this.AutoScroll = 
        Math.trunc( this.Toolbar.GetDResource( "ToggleAutoScroll/OnState" ) );
   }
   else    // Set to the supplied value. 
   {
      this.AutoScroll = new_value;
      this.Toolbar.SetDResource( "ToggleAutoScroll/OnState", this.AutoScroll );
   }
    
   /* Set chart's auto-scroll. AutoScroll property is constrained for
      all charts, so we can use the first chart.
   */
   this.ChartArray[ 0 ].chart.SetDResource( "AutoScroll", this.AutoScroll );

   /* Set the state of the AutoScroll checkbox in HTML. */
   CheckElement( this.GLG_div_name + "_auto_scroll", this.AutoScroll != 0 );
}

//////////////////////////////////////////////////////////////////////////////
// Changes action to be performed on touch move on mobile devices.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SetTouchAction = function()
{
   let id = this.GLG_div_name + "_scroll_mode";
   let scroll_mode_elem = document.getElementById( id );
   if( scroll_mode_elem )     
     this.ScrollMode = scroll_mode_elem.checked;
   else
     AppError( "Can't find element: " + id );
}

//////////////////////////////////////////////////////////////////////////////
// Change Y Dragging mode.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ChangeYDragging = function( /*int*/ new_value )
{
   if( new_value == -1 )  // Use the state of the ToggleYDragging button.
   {
      this.EnableYDragging = 
        Math.trunc( this.Toolbar.GetDResource( "ToggleYDragging/OnState" ) );
   }
   else    // Set to the supplied value. 
   {
      this.EnableYDragging = new_value;
      this.Toolbar.SetDResource( "ToggleYDragging/OnState",
                                 this.EnableYDragging );
   }
}

//////////////////////////////////////////////////////////////////////////////
// Changes the time span shown in the TimeAxis. span_index is the 
// current SelectedIndex of the SpanSelector combobox widget in the 
// chart's toolbar.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SetChartSpan = function( /*int*/ span_index )
{
   if( span_index > SpanData.length )
   {
      AppError( "Ivalid span_index." );
      return;
   }

   let span_info = SpanData[ span_index ];     /* SpanInfo */

   /* Update the SpanSelector menu in the drawing with the initial value
      if different.
   */
   this.Toolbar.SetDResource( "SpanSelector/SelectedIndex", span_index, true );

   /* Set intervals before SetZoom() below to avoid redrawing huge number
      of labels.
   */ 
   this.TimeAxis.SetDResource( "MajorInterval", span_info.major_interval );
   this.TimeAxis.SetDResource( "MinorInterval", span_info.minor_interval );
    
   /* Set Time axis span, which controls how much data is displayed 
      in the chart. 
   */
   this.TimeSpan = span_info.span;
   this.TimeAxis.SetDResource( "Span", this.TimeSpan );
    
   // Calculate scroll increment in seconds based on the TimeSpan.
   this.ScrollIncrement = this.TimeSpan / 30.0;

   /* Turn on data filtering for large spans. 
      FilterType and FilterPrecision attributes are Global in the 
      ChartSeries and therefore are constrained for all chart plots, 
      so we can set these resources only for the first chart.
   */
   let plot = this.ChartArray[ 0 ].plot_info.plot; /* GlgObject */
   if( span_info.span >= ONE_MINUTE )
   {
      /* Agregate multiple data samples to minimize a number of 
         data points drawn per each FilterPrecision interval. 
         Show only one set of MIN/MAX values per each pixel interval. 
      */
      plot.SetDResource( "FilterType", GLG.GlgChartFilterType.MIN_MAX_FILTER );
      plot.SetDResource( "FilterPrecision", 2.0 );
   }
   else
     plot.SetDResource( "FilterType", GLG.GlgChartFilterType.NULL_FILTER );

   /* Set TooltipFormat for each chart if supplied. Otherwise, 
      use TooltipFormat as defined in the drawing. 
      TooltipFormat is constrained for all charts, so we can use 
      the first chart to set the attribute.
   */
   if( span_info.tooltip_format != null )
     this.ChartArray[ 0 ].chart.SetSResource( "TooltipFormat",
                                              span_info.tooltip_format );
}

//////////////////////////////////////////////////////////////////////////////
// Restore Y axis range to the initial Low/High values.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.RestoreInitialYRanges = function()
{
   for( let i=0; i<this.NumCharts; ++i )
   {
      let chart = this.ChartArray[ i ].chart;  /* GlgObject */
      chart.SetDResource( "YAxis/Low", this.ChartArray[ i ].low );
      chart.SetDResource( "YAxis/High", this.ChartArray[ i ].high );
   }
}
   
//////////////////////////////////////////////////////////////////////////////
// Returns the chart viewport which is ZoomToMode, if any.
// ZoomToMode is activated on Dragging and ZoomTo operations.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.ZoomToMode = function()   /* GlgObject */
{
   for( let i=0; i<this.NumCharts; ++i )
   {
      let zoom_mode = 
        Math.trunc( this.ChartArray[ i ].chart_vp.GetDResource( "ZoomToMode" ) );

      if( zoom_mode != 0 )
        return this.ChartArray[ i ].chart_vp;
   }

   return null;
}

//////////////////////////////////////////////////////////////////////////////
// Abort ZoomTo mode.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.AbortZoomTo = function()
{
   let zoom_viewport = this.ZoomToMode();  /* GlgObject */
   if( zoom_viewport == null )
     return;
    
   // Abort zoom mode in progress.
   zoom_viewport.SetZoom( null, 'e', 0.0 ); 
   zoom_viewport.Update();
}

////////////////////////////////////////////////////////////////////////
// Display a vertical cross-hair line at cursor position, as well as
// annotations for each chart.
////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.DisplayCrossHairLine =
  function( /*GlgPoint*/ cursor_pos )
{
   if( cursor_pos == null )
   {
      this.DisableCrossHairAndAnnotations();
      return;
   } 
    
   /* Obtain time value at cursor position, taking into account only 
      X direction. Use the first chart, since all charts have a 
      common Time axis.
   */
   let time_stamp = /* double */
     this.ChartArray[ 0 ].chart.PositionToValue( null, cursor_pos.x,
                                                 cursor_pos.y,
                                                 true /*X direction*/,
                                                 false /*Y direction*/ );
    
   /* Disable the cross-hair line and all annotations if the cursor 
      is outside the chart area in horizontal direction (time_stamp=null).
   */
   if( time_stamp == null )
   {
      this.DisableCrossHairAndAnnotations();
      return;
   }
    
   /* Cursor is either inside one of the charts or inside the time axis: 
      Enable the cross-hair lines and position them using the new 
      time stamp, so that the cross-hair follows the mouse cursor.
   */
   this.UpdateCrossHair( true, time_stamp );
    
   /* In each chart, highlight a datasample nearest to the time stamp
      and display its value.
   */
   this.DisplayAnnotations( time_stamp );
    
   // Refresh display.
   this.MainViewport.Update();
}

//////////////////////////////////////////////////////////////////////////
// Disable/enable cross hair line. Cross hair line parameters are
// constrained for all charts, so positioning the cross hair line in the
// first chart will position cross hair lines in all charts.
//////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.UpdateCrossHair =
  function( /*boolean*/ enable, /*double*/ time_stamp )
{
   let cross_hair = this.ChartArray[ 0 ].cross_hair_line;  /* GlgObject */
   if( cross_hair == null )
     return;
    
   // Enable/disable cross hair based on the enable flag.
   cross_hair.SetDResource( "Enabled", enable ? 1.0 : 0.0 );
    
   // Update cross hair position. 
   if( enable )
     cross_hair.SetDResource( "Time", time_stamp );
}

///////////////////////////////////////////////////////////////////////////
// In each chart, find a datasample nearest to the time stamp, highlight it
// using a marker annotaion, and display its value using a label annotation.
///////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.DisplayAnnotations = function( /*double*/ time_stamp )
{
   for( let i=0; i<this.NumCharts; ++i )
   {
      let plot_point = /* GlgPoint */
        GetPlotValueAtTimeStamp( this.ChartArray[ i ], time_stamp );
        
      if( plot_point == null )
        // Data sample not found, disable annotations for this chart.
        UpdateChartAnnotations( this.ChartArray[ i ], false /*disable*/,
                                null, -1 );
      else
        // Found data sample, enable and update chart annotations.
        UpdateChartAnnotations( this.ChartArray[ i ], true /*enable*/,
                                plot_point, time_stamp );
   }
}

///////////////////////////////////////////////////////////////////////////
// Finds a chart datasample nearest to the time stamp and returns its
// X/Y values. Returns null if not found. 
///////////////////////////////////////////////////////////////////////////
function GetPlotValueAtTimeStamp( /*ChartInfo*/ chart_info,
                                  /*double*/ time_stamp )   /* GlgPoint */
{
   if( chart_info == null )
     return null;
    
   let chart = chart_info.chart;         /* GlgObject */
   let plot = chart_info.plot_info.plot; /* GlgObject */
   if( plot == null )
     return null;
    
   /* Find plot's data sample nearest to specified time stamp within
      the dx search interval.  
   */
   let dx = 5.0; // double: 5 sec search interval for X axis
   let dy = 0;   // double: Not used since we ignore Y direction in the search.
   let selection = /* GlgObject */ 
     chart.CreateChartSelection( plot, time_stamp, 0, dx, dy,
                                 false /* use time/value instead of screen coords */, 
                                 false /* exclude invalid points */,
                                 true /* nearest in X direction */ );
    
   if( selection == null )
     return null; // No valid data sample found.
 
   // Query X/Y values of the selected data sample.
   let x_value = selection.GetDResource( "SampleX" ); /* double */
   let y_value = selection.GetDResource( "SampleY" ); /* double */
    
   return GLG.CreateGlgPoint( x_value, y_value, 0.0 );
}

///////////////////////////////////////////////////////////////////////////
// Update parameters of the chart annotations.
///////////////////////////////////////////////////////////////////////////
function UpdateChartAnnotations( /*ChartInfo*/ chart_info, /*boolean*/ enable,
                                 /*GlgPoint*/ plot_point, /*double*/ pos_x )
{
   if( chart_info == null )
     return;
    
   // Update LabelAnnotation.
   let label_annotation = chart_info.label_annotation; /* GlgObject */
   if( label_annotation != null )
   {
      label_annotation.SetDResource( "Enabled", enable ? 1.0 : 0.0 );        
      if( enable )
      {
         label_annotation.SetDResource( "Value", plot_point.y );
         label_annotation.SetDResource( "Time", plot_point.x );
         label_annotation.SetDResource( "PositionX", pos_x );
      }
   }
    
   // Update MarkerAnnotation.
   let marker_annotation = chart_info.marker_annotation;  /* GlgObject */
   if( marker_annotation != null )
   {
      marker_annotation.SetDResource( "Enabled", enable ? 1.0 : 0.0 );        
      if( enable )
      {
         marker_annotation.SetDResource( "Value", plot_point.y );
         marker_annotation.SetDResource( "Time", plot_point.x );
      }
   }
}

///////////////////////////////////////////////////////////////////////////
// Disable cross-hair line and annotations for all charts.
///////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.DisableCrossHairAndAnnotations = function()
{
   this.UpdateCrossHair( false, -1 ); // Disable cross hair.
    
   // Disable annotations for all charts.
   this.DisableAllAnnotations();
}

///////////////////////////////////////////////////////////////////////////
// Disable annotations for all charts.
///////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.DisableAllAnnotations = function()
{
   // Disable all annotations.
   for( let i=0; i<this.NumCharts; ++i )
     UpdateChartAnnotations( this.ChartArray[ i ], false /*disable*/,
                             null, -1 );
}

//////////////////////////////////////////////////////////////////////////////
// Determines a good number of extra seconds to be added at the end in
// the real-time mode to show a few next updates without scrolling the
// chart.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.GetExtraSeconds = function()   /* double */
{
   let extra_sec, max_extra_sec;   /* double */

   extra_sec = this.TimeSpan * 0.1;
   max_extra_sec = ( this.TimeSpan > ONE_HOUR ? 5.0 : 3.0 );

   if( extra_sec > max_extra_sec )
     extra_sec = max_extra_sec;
   
   return extra_sec;
}

//////////////////////////////////////////////////////////////////////////////
// Set dialog size and position the dialog in the parent div.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SetDialogSizeAndPosition =
  function( /*GlgObject*/ dialog )
{
   if( dialog == null )
     return;
   
   let row_height = 30;
   let height =
      ( row_height * this.ChartMenuNumVisRows + 55 ) * this.CoordScale;
   let width = 180 * this.CoordScale;

   // Obtain parent div.
   let glg_area = document.getElementById( this.GLG_div_name );

   /* Position the dialog off-center in the parent div (glg_area).
      Offset is relative to the parent viewport.
   */
   let offset_x = glg_area.clientWidth / 4 * this.CoordScale;
   let offset_y = glg_area.clientHeight / 4 * this.CoordScale;

   ConfigureWindow( dialog, offset_x, offset_y, width, height );
}

//////////////////////////////////////////////////////////////////////////
// Returns data sample querying interval (in sec.).
//////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.GetSampleInterval = function()  /* double */
{
   /* Use sata sampling interval (in seconds) corresponding to the specified
      UpdateInterval. The Update interval is defined in millisec.
      The application may specify data sampling interval as needed.
   */
   let interval = UPDATE_INTERVAL / 1000.0;  /* double */
   return interval; 
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 700 / 600;
   const ASPECT_RATIO_MOBILE = 700 / 800;
 
   const MIN_WIDTH = 500;
   const MAX_WIDTH = 900;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];

         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height = "" +
        Math.trunc( drawing_area.clientWidth / ASPECT_RATIO_MOBILE ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      TextScale = 1.6;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 1;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
// Loads any assets required by the application and invokes the specified
// callback when done.
// Alternatively, the application drawing can be loaded as an asset here
// as well, so that it starts loading without waiting for the other assets 
// to finish loading.
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.LoadAssets = function( callback, user_data )
{
   Debug( "LoadAssets for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Define an internal variable to keep the number of loaded assets. */
   this.NumLoadedAssets = 0;

   /* HTML5 doesn't provide a scrollbar input element (only a range input 
      html element is available). This application needs to load GLG scrollbars
      used for integrated chart scrolling. For each loaded scrollbar, the 
      AssetLoaded callback is invoked with the supplied data array parameter.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_h.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_h", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_v.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_v", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );   

   /* Load a GLG drawing from the specified drawing file. */
   let drawing_file = ( this.IsMobile ? DRAWING_NAME_MOBILE : DRAWING_NAME );
   GLG.LoadWidgetFromURL( this.GetFullName( drawing_file ),
                          null, this.AssetLoaded.bind( this ),
                          { name: "drawing_file", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
}

//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.AssetLoaded = function( glg_object, data, path )
{
   if( !this.Active )
     return;
   
   if( data.name == "scrollbar_h" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgHScrollbar", glg_object );
   }
   else if( data.name == "scrollbar_v" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgVScrollbar", glg_object );
   }
   else if( data.name == "drawing_file" )
   {
      // Chart drawing is loaded: perform needed initialization.
      this.Init( glg_object );
   }
   else
     AppError( "Unexpected asset name" );

   ++this.NumLoadedAssets;
    
   // Invoke the callback after all assets have been loaded.
   if( this.NumLoadedAssets == 3 )
   {
      this.AssetsLoadedFlag = true;
      data.callback( data.user_data );
   }
}

//////////////////////////////////////////////////////////////////////////////
// Status display: 
// Display the title of the currently loaded configuration, as well as
// the title of the configuration which is in the process of being
// loaded (if any).
//////////////////////////////////////////////////////////////////////////////
GlgComboChart.prototype.DisplayStatus = function()
{
   let message;
   
   if( this.LoadedConfigInfo == null && this.NewConfigTitle == null )
     message = "<br>";
   else
   {
      message = "";
      if( this.LoadedConfigInfo )
        message +=
          "Displayed: <b>" + this.LoadedConfigInfo.config_title + "</b>";
        
      if( this.NewConfigTitle )
      {
         if( this.LoadedConfigInfo )
           // Add spaces after the displayed configuration title.
           message += "&nbsp;&nbsp;&nbsp;&nbsp;";   
         
         message += "Loading: <b>" + this.NewConfigTitle + "</b>";
      }
   }

   document.getElementById( this.GLG_div_name + "_status_div" ).innerHTML = message;
}

//////////////////////////////////////////////////////////////////////////
function ConfigRecord( /*String*/ chart_title, /*String*/ yaxis_label,
                       /*String*/ tag, /*double*/ low, /*double*/ high,
                       /*{r,g,b}*/ plot_color, /*boolean*/ init_visibility )
{
   this.chart_title = chart_title;
   this.yaxis_label = yaxis_label;
   this.tag = tag;
   this.low = low;
   this.high = high;
   this.plot_color = plot_color;
   this.init_visibility = init_visibility;
}

////////////////////////////////////////////////////////////////////////
function SpanInfo( /*String*/ label, /*int*/ time_span,
                   /*double*/ major_interval, /*double*/ minor_interval, 
                   /*String*/ time_format, /*String*/ tooltip_format )
{
   this.label = label;
   this.span = time_span;
   this.major_interval = major_interval;
   this.minor_interval = minor_interval;
   this.time_format = time_format;
   this.tooltip_format = tooltip_format;
}

////////////////////////////////////////////////////////////////////////
// The folowing properies will be added to the ChartInfo object by the
// InitializeChart method:
//   chart_vp          - stored the GLG object ID of the chart's viewport
//   chart             - stored the GLG object ID of the chart object
//   plot_info         - PlotInfo object that stored plot information
//   low, high         - double values that store YAxis ranges
//   cross_hair_line   - stores the GLG object ID of the vertical line
//                       that follows the mouse position, is stored only
//                       for the first chart.
//   label_annotation  - stores the GLG object ID of the LabelAnnotation
//                       object used to display custom tooltip showing
//                       value and time of the plot's data sample at the 
//                       mouse position.
//   marker_annotation - stores the GLG object ID of the MarkerAnnotation
//                       object used to annotate the data sample selected
//                       in the plot.
////////////////////////////////////////////////////////////////////////    
function ChartInfo( /*GlgObject*/ chart_vp )
{
}

////////////////////////////////////////////////////////////////////////
// Should be invoked after hierarchy setup.
////////////////////////////////////////////////////////////////////////
function InitializeChart( /*GlgObject*/ chart_vp,
                          /*ConfigRecord*/ config_record )
{
   if( config_record == null )
     return null;

   let chart_info = new ChartInfo();
   
   // Store the chart viewport.
   chart_info.chart_vp = chart_vp;     /* GlgObject */
   if( chart_vp == null )
   {
      AppError( "Invalid chart viewport." );
      return;
   }
   
   // Store the chart object inside the chart's viewport.
   chart_info.chart = chart_vp.GetResourceObject( "Chart" );  /*GlgObject*/
    
   // Store chart range to be able to restore it after zoom.
   chart_info.low = config_record.low;
   chart_info.high = config_record.high;
   chart_info.SetChartRange();
   
   /* Set ChartZoomMode for the viewport, in case it hasn't been set in the
      drawing.
   */
   chart_info.chart_vp.SetZoomMode( null, chart_info.chart, null,
                                    GLG.GlgZoomMode.CHART_ZOOM_MODE );
    
   // Set Y axis label.
   chart_info.chart.SetSResource( "YAxis/AxisLabelString",
                                  config_record.yaxis_label );
   // Set chart title.
   chart_info.chart_vp.SetSResource( "Title/String",
                                     config_record.chart_title );
   
   // Initialize the chart's plot.
   chart_info.InitPlot( config_record.tag, config_record.plot_color );
   
   /* Initializes label and marker annotations and stores them as
      label_annotation and marker_annotation properties of chart_info.
   */
   chart_info.InitAnnotations();
   
   // Set chart's initial visibility.
   chart_info.ShowChart( config_record.init_visibility );
   
   return chart_info;
}

////////////////////////////////////////////////////////////////////////
// Set chart's low and high range to the stored values.
// Assumes the chart has one YAxis and CommonRange=YES.
////////////////////////////////////////////////////////////////////////
ChartInfo.prototype.SetChartRange = function()
{
   this.chart.SetDResource( "YAxis/Low", this.low );
   this.chart.SetDResource( "YAxis/High", this.high );
}

////////////////////////////////////////////////////////////////////////
// Should be invoked after hierarchy setup.
////////////////////////////////////////////////////////////////////////
ChartInfo.prototype.InitPlot = function( /*String*/ tag, /*GlgPoint*/ color )
{
    /* This example assumes that each chart has one plot and one Y axis.
       The code may be extended to support multiple plots and multiple 
       Y axes as needed.
    */

    // Obtain and store object ID of a chart's plot.
    let plot_array = this.chart.GetResourceObject( "Plots" ); /* GlgObject */  
    let plot = plot_array.GetElement( 0 );                    /* GlgObject */
    this.plot_info = new PlotInfo( plot, tag, color );
}

////////////////////////////////////////////////////////////////////////
ChartInfo.prototype.InitCrossHairLine = function()
{
   let time_lines =
   this.chart.GetResourceObject( "TimeLines" ); /* GlgObject */
   if( time_lines == null )
     return;
    
   /* Retrieve CrossHairLine, used to draw a vertical crosshair line that 
      follows the mouse cursor.
   */
   this.cross_hair_line = time_lines.GetResourceObject( "CrossHairLine" );
   if( this.cross_hair_line == null )
   {
      AppError( "CrossHairLine object not found." );
      return;
   }
   
   // Disable time_line by default. It will be enabled on mouse move.
   this.cross_hair_line.SetDResource( "Enabled", 0.0 );
}

////////////////////////////////////////////////////////////////////////
ChartInfo.prototype.InitAnnotations = function()
{
   /* Retrieve annotation objects, if any. 
      LabelAnnotation moves with the time line and displays time and value 
      of the closest plot data sample. MarkerAnnotation highlights the found 
      data sample, if any.
    */
   let annotations =
     this.chart.GetResourceObject( "Annotations" ); /* GlgObject */    
   if( annotations == null )
     return;
    
   this.label_annotation = annotations.GetResourceObject( "LabelAnnotation" );
   if( this.label_annotation != null )
     // Disable label annotation by default.
     this.label_annotation.SetDResource( "Enabled", 0.0 );
   else
     AppError( "LabelAnnotation object not found." );
    
   this.marker_annotation = annotations.GetResourceObject( "MarkerAnnotation" );
   if( this.marker_annotation != null )
     // Disable marker annotation by default.
     this.marker_annotation.SetDResource( "Enabled", 0.0 );
   else
     AppError( "MarkerAnnotation object not found." );
}

////////////////////////////////////////////////////////////////////////
// Show/hide invididual chart.
////////////////////////////////////////////////////////////////////////
ChartInfo.prototype.ShowChart = function ( /*bool*/ show )
{
   this.chart_vp.SetDResource( "Visibility", show ? 1.0 : 0.0 );
}

////////////////////////////////////////////////////////////////////////
function PlotInfo( /*GlgObject*/ plot, /*String*/ tag, /*{r,g,b}*/ color )
{
   this.plot = plot;   // Store object ID of the chart's plot (Plot#0). */    
   this.tag = tag;     // Store data tag associated with the chart's plot.
    
   // Store plot's ValueEntryPoint.
   this.value_ep = /*GlgObject*/
        this.plot.GetResourceObject( "ValueEntryPoint" );

   // Store plot's TimeEntryPoint.
   this.time_ep = /* GlgObject */
     this.plot.GetResourceObject( "TimeEntryPoint" );

   // Store plot's ValidEntryPoint.
   this.valid_ep = /* GlgObject */
     this.plot.GetResourceObject( "ValidEntryPoint" );
        
   // Set plot's line color.
   if( color != null )
     this.plot.SetGResource( "EdgeColor", color.r, color.g, color.b );
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     AppLog( message );
}
